import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  takeUntil,
} from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { XbrlDataTransformerService } from '../../../services/mbrs/xbrl-data-transformer.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { EnvironmentService } from '../../../services/common/environment.service';
import { WarningResult } from '../../../models/validation-warning.model';
import { FormSchema, FormControlSchema } from '../../../models/form-schema.model';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { WarningMessageComponent, WarningMessage } from "../../common/warning-message/warning-message.component";
import { handleDateInputChange } from '../../../utils/helpers';
import {
  FORM_AUTOSAVE_DEBOUNCE_MS,
  WARNING_EVALUATION_DEBOUNCE_MS,
} from '../../../utils/debounce-times.util';
import {
  isRole2UnconditionallyRequired,
  ROLE2_REQUIRED_AXIS,
} from '../../../services/mbrs/validators/role2-scope-of-filing.rules';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import { coerceSchemaDateStrings } from '../../../utils/date-string-coercion.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
@Component({
  selector: 'app-role2-020000-scope-of-filing',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Select,
    SelectClearIconBehaviorDirective,
    DatePicker,
    ButtonModule,
    TooltipModule,
    Toast,
    SubmitForValidationComponent,
    WarningMessageComponent,
    PlaceholderPipe,
    FixedPlaceholderPipe,
    FieldLinkTargetDirective
],
  providers: [MessageService],
  templateUrl: './role2-020000-scope-of-filing.component.html',
  styleUrls: ['./role2-020000-scope-of-filing.component.css'],
})
export class Role2020000ScopeOfFilingComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private xbrlTransformer = inject(XbrlDataTransformerService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  scopeForm!: FormGroup;
  schema!: FormSchema;

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};
  typeDict: any = {};
  isLoading = true;

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // MSIC Code options
  msicOptions: any[] = [];

  // Flag to track if we're loading saved state (to prevent auto-save during load)
  private isLoadingState = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Scope of Filing (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.scopeOfFiling
    );
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '2',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      await this.loadSchema();
      await this.loadTypeDict();
      await this.loadMsicOptions();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupWarningEvaluation(); // Evaluate warnings on value changes
      this.evaluateFieldWarnings(); // Evaluate warnings on initial load

      // Deterministic finalization (replaces the legacy chained
      // setTimeout(100 → 600) that used to live in loadSavedState).
      // Extracted to finalizeInit() so the non-init callers (onCancel /
      // onRevert / onCancelConfirmed) can re-finalize after their own
      // loadSavedState() calls — see finalizeInit's doc comment.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Deterministic sheet-init finalization. Must run after EVERY loadSavedState()
   * (init AND cancel/revert) — the legacy setTimeout lived inside loadSavedState
   * and re-finalized on each call; this method restores that for the non-init
   * callers. Safe because all init-time patches use emitEvent:false.
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.scopeOfFiling,
      form: this.scopeForm,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
      persistInitial: () => {
        this.stateService.updateSheetData(
          SHEET_KEYS.scopeOfFiling,
          this.scopeForm.getRawValue(),
          this.scopeForm.valid,
          true,
        );
      },
    });
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   * Skips processing when in pre-validation mode to prevent infinite loops
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.scopeOfFiling),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((scopeOfFilingData) => {
        // Skip processing during pre-validation to prevent infinite loops
        if (this.stateService.isPreValidating()) {
          return;
        }

        if (scopeOfFilingData && Object.keys(scopeOfFilingData).length > 0) {
          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // CLEANUP: Remove stale nested business member objects from state.
          // When API data is loaded, it contains nested format like:
          //   "ssmt:MSICCode": { "BusinessOneMember": "01111" }
          // After user edits, bracket notation is added:
          //   "ssmt:MSICCode[ssmt:BusinessOneMember]": "47112"
          // Both co-exist in localStorage. Clean up the stale nested objects to prevent
          // duplicate data on submit.
          const cleanedData = this.xbrlTransformer.removeStaleBusinessMemberObjects(scopeOfFilingData);

          // If cleanup removed stale objects, persist the cleaned state back to localStorage
          if (Object.keys(cleanedData).length !== Object.keys(scopeOfFilingData).length) {
            this.stateService.setSheetData(SHEET_KEYS.scopeOfFiling, cleanedData);
          }

          // CRITICAL: Transform backend business member format to frontend bracket format
          // Backend: { "ssmt:MSICCode": { "businessOneMember": "70209" } }
          // Frontend: { "ssmt:MSICCode[ssmt:BusinessOneMember]": "70209" }
          const transformedData = this.xbrlTransformer.transformBusinessMembersToFrontend(cleanedData);

          // Convert date strings to Date objects (same as filing-info pattern)
          const convertedData =
            this.convertDateStringsToDateObjects(transformedData);

          // Patch the form with the new data
          this.scopeForm.patchValue(convertedData, { emitEvent: false });

          // Look up and populate descriptions from MSIC codes
          // This ensures DescriptionOfBusiness fields show the actual description text
          // rather than raw codes that may have been stored by the API
          this.populateDescriptionsFromMsicCodes();

          // IMPORTANT: Re-apply inherited fields from Role-1 after patching backend data
          // This ensures inherited fields (stock exchange, Bank Negara, methods, etc.)
          // always come from Role-1 and are not overwritten by backend data
          this.loadInheritedFields();

          // Evaluate field warnings after data is loaded
          this.evaluateFieldWarnings();

          // Sync the form's complete state (including inherited fields, converted Date objects)
          // to the modified state so the snapshot baseline will match getRawValue()
          this.stateService.updateSheetData(
            SHEET_KEYS.scopeOfFiling,
            this.scopeForm.getRawValue(),
            this.scopeForm.valid,
            true // skipEditedMark
          );

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        } else {
          console.warn('⚠️ Role2: Empty or invalid data received from state listener');
        }
      });
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-2.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach((key) => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map(
        (value: string) => ({
          label: value,
          value: value,
        })
      );
    });
  }

  /**
   * Load MSIC Code options from JSON file
   */
  private async loadMsicOptions(): Promise<void> {
    try {
      const options = await fetchJsonAsset<unknown>('/assets/msic_options.json');
      if (!Array.isArray(options) || options.length === 0) {
        throw new Error('msic_options.json loaded but holds no options');
      }
      this.msicOptions = options;
    } catch (error) {
      // DEGRADED, not fatal (developer ruling, 2026-10-26): the MSIC dropdowns open
      // empty, the sheet still loads and no new submit block is added. Report it
      // with `warn`, never `fail`: `fail` closes the trace, so a genuine failure
      // later in this same init (a BUILD) would be swallowed and the entry stall
      // clock cancelled. The cause carries the underlying class (NET / ASSET /
      // PARSE / empty list) for the console record.
      this.msicOptions = [];
      this.loadTraceHandle.warn('EMPTY', error);
    }
  }

  /**
   * Handle MSIC Code selection change - auto-populate description.
   *
   * A null/empty code means the user cleared the dropdown (PrimeNG's
   * Select.clear() emits onChange with `value: null`). Code and description are
   * a derived pair, so clearing the code must blank the description too —
   * otherwise the submission carries a description with no matching MSIC code.
   */
  onMsicCodeChange(event: any, businessNumber: number): void {
    const selectedCode = event.value;

    // Map business number to member axis
    const memberMap: { [key: number]: string } = {
      1: 'ssmt:BusinessOneMember',
      2: 'ssmt:BusinessTwoMember',
      3: 'ssmt:BusinessThreeMember'
    };
    const member = memberMap[businessNumber];
    if (!member) return;

    const descField = `ssmt:DescriptionOfBusiness[${member}]`;

    if (!selectedCode) {
      this.scopeForm.patchValue({ [descField]: '' });
      return;
    }

    const selectedMsic = this.msicOptions.find(
      (opt) => opt.code === selectedCode
    );
    if (selectedMsic) {
      this.scopeForm.patchValue({
        [descField]: selectedMsic.descriptionEn,
      });
    }
  }

  /**
   * Look up MSIC descriptions for all business members based on their current code values
   * and populate the corresponding DescriptionOfBusiness fields.
   *
   * This is needed because when data is loaded from the API or localStorage,
   * the onMsicCodeChange event handler does not fire (it only fires on user interaction).
   * The API may store raw codes in DescriptionOfBusiness instead of the actual descriptions,
   * so we always re-derive descriptions from the MSIC code lookup.
   */
  private populateDescriptionsFromMsicCodes(): void {
    if (!this.msicOptions || this.msicOptions.length === 0) {
      return;
    }

    const memberFields = [
      { codeField: 'ssmt:MSICCode[ssmt:BusinessOneMember]', descField: 'ssmt:DescriptionOfBusiness[ssmt:BusinessOneMember]' },
      { codeField: 'ssmt:MSICCode[ssmt:BusinessTwoMember]', descField: 'ssmt:DescriptionOfBusiness[ssmt:BusinessTwoMember]' },
      { codeField: 'ssmt:MSICCode[ssmt:BusinessThreeMember]', descField: 'ssmt:DescriptionOfBusiness[ssmt:BusinessThreeMember]' },
    ];

    const patchData: Record<string, string | null> = {};

    memberFields.forEach(({ codeField, descField }) => {
      const codeValue = this.scopeForm.get(codeField)?.value;
      if (codeValue) {
        const msicOption = this.msicOptions.find((opt) => opt.code === codeValue);
        if (msicOption) {
          patchData[descField] = msicOption.descriptionEn;
        }
        // If no matching MSIC option found, preserve the code from the API as-is.
        // Don't clear it — the code may be valid but not in the local options list.
      }
    });

    if (Object.keys(patchData).length > 0) {
      this.scopeForm.patchValue(patchData, { emitEvent: false });
    }
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }
    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      const validators = [];

      // Required validator — honours BOTH `control.required === true` AND an
      // unconditional `{ name: 'required' }` entry in the validators array.
      // Detector lives in role2-scope-of-filing.rules so the pure validator
      // stays in lockstep. See role 6's equivalent fix for the original
      // latent-bug context: the old check only honoured the top-level flag
      // and silently dropped two required fields declared via validators[].
      if (isRole2UnconditionallyRequired(control)) {
        validators.push(Validators.required);
      }

      // Add pattern validator if specified
      const patternValidator = control.validators?.find(
        (validator) => validator.name === 'pattern'
      );
      if (patternValidator && patternValidator.patternSource) {
        const patternKey = patternValidator.patternSource.replace('type:', '');
        const pattern = this.typeDict.patterns?.[patternKey];
        if (pattern) {
          validators.push(Validators.pattern(pattern));
        }
      }

      // Get default value
      let defaultValue = '';
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Build control name with dimensional naming if field has allowedAxes
      if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
        // Create form controls for ALL axes (e.g., Business 1, 2, 3)
        control.ContextPolicy.allowedAxes.forEach(axis => {
          const controlName = `${control.qname}[${axis}]`;

          // Only BusinessOneMember (the primary business row) is required on
          // the form. CLBG-axis fields (ObjectOne/Two/Three) deliberately do
          // NOT get `Validators.required` here. Shared axis constant keeps
          // the pure validator in sync.
          // Developer Note (TODO): nothing filters CLBG-vs-non-CLBG fields out
          // of the submitted payload. The `FormSanitizationService` this
          // comment used to cite was never wired in and was deleted as dead
          // code on 2026-10-21. Confirm with the P.O. whether a filter is needed.
          const axisFormControl = new FormControl(
            { value: defaultValue, disabled: control.ui.readonly || false },
            axis === ROLE2_REQUIRED_AXIS && isRole2UnconditionallyRequired(control)
              ? [Validators.required]
              : []
          );

          formControls[controlName] = axisFormControl;
        });
      } else {
        // No axes - create regular control
        const formControl = new FormControl(
          { value: defaultValue, disabled: control.ui.readonly || false },
          validators
        );
        formControls[control.qname] = formControl;
      }
    });

    this.scopeForm = new FormGroup(formControls);
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.scopeOfFiling);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling);

    if (savedData && Object.keys(savedData).length > 0) {

      // CLEANUP: Remove stale nested business member objects from saved state.
      // This prevents duplicate data accumulation in localStorage.
      const cleanedData = this.xbrlTransformer.removeStaleBusinessMemberObjects(savedData);

      // If cleanup removed stale objects, persist the cleaned state back to localStorage
      if (Object.keys(cleanedData).length !== Object.keys(savedData).length) {
        this.stateService.setSheetData(SHEET_KEYS.scopeOfFiling, cleanedData);
      }

      // CRITICAL: Transform backend business member format to frontend bracket format
      // This handles cases where data was saved in backend format or loaded from API
      const transformedData = this.xbrlTransformer.transformBusinessMembersToFrontend(cleanedData);

      // Convert date strings back to Date objects for date picker fields
      const convertedData = this.convertDateStringsToDateObjects(transformedData);

      this.scopeForm.patchValue(convertedData, { emitEvent: false });

      // Look up and populate descriptions from MSIC codes
      // This ensures DescriptionOfBusiness fields show the actual description text
      // rather than raw codes that may have been stored by the API
      this.populateDescriptionsFromMsicCodes();
    }

    // Load inherited fields from Role-1 (Filing Info)
    this.loadInheritedFields();

    // NOTE: the initial-persist + isLoadingState flip + baseline snapshot
    // (markSheetInitializedWithStatus) are now owned by finalizeSheetInit(),
    // called deterministically from ngOnInit after waitForMbrsHydration().
    // We keep ONLY the synchronous setup above (markSheetInitializing, the
    // emitEvent:false patches, inherited-field load) here.
  }

  /**
   * Convert date strings from localStorage or API back to Date objects
   * PrimeNG DatePicker requires Date objects, not strings
   *
   * Supports multiple date string formats:
   * - "dd/MM/yyyy" (API format, e.g., "31/01/2025" = January 31, 2025)
   * - ISO 8601 / other formats parseable by Date constructor (e.g., "2025-01-31T00:00:00")
   */
  /**
   * Convert date strings from persisted/API state back to Date objects.
   *
   * Delegates to the shared `coerceSchemaDateStrings` — see that util's
   * docblock for why an unconverted string renders as a BLANK
   * `<p-datepicker>`, and for which state slice carries which shape. The
   * other filing-info sheets carry the identical one-liner; keep them in step.
   */
  private convertDateStringsToDateObjects(
    data: Record<string, any>
  ): Record<string, any> {
    return coerceSchemaDateStrings(data, this.schema?.controls);
  }

  /**
   * Load inherited fields from Role-1 (Filing Info)
   */
  private loadInheritedFields(): void {
    const filingInfoData = this.stateService.getActiveFilingInfoData();

    if (filingInfoData) {
      const inheritedFields: Record<string, any> = {};

      // Inherit stock exchange disclosure from Role-1
      const stockExchangeValue =
        filingInfoData[
          'ssmt:DisclosureOnWhetherCompanysSharesAreTradedOnAnyOfficialStockExchange'
        ];
      if (stockExchangeValue) {
        inheritedFields[
          'ssmt:DisclosureOnWhetherCompanysSharesAreTradedOnAnyOfficialStockExchange'
        ] = stockExchangeValue;
      }

      // Inherit Bank Negara regulation from Role-1
      const bankNegaraValue =
        filingInfoData[
          'ssmt:DisclosureOfWhetherCompanyRegulatedByBankNegaraMalaysiaAtFinancialYearEnd'
        ];
      if (bankNegaraValue) {
        inheritedFields[
          'ssmt:DisclosureOfWhetherCompanyRegulatedByBankNegaraMalaysiaAtFinancialYearEnd'
        ] = bankNegaraValue;
      }

      // Inherit exemption/waiver description from Role-1
      const exemptionValue =
        filingInfoData[
          'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister'
        ];
      if (exemptionValue) {
        inheritedFields[
          'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister'
        ] = exemptionValue;
      }

      // Inherit method for preparing Statement of Financial Position
      const methodFinPosValue =
        filingInfoData[
          'ssmt:MethodUsedForPreparingStatementOfFinancialPosition'
        ];
      if (methodFinPosValue) {
        inheritedFields[
          'ssmt:MethodUsedForPreparingStatementOfFinancialPosition'
        ] = methodFinPosValue;
      }

      // Inherit method for preparing Statement of Profit or Loss
      const methodPnLValue =
        filingInfoData['ssmt:MethodUsedForPreparingStatementOfProfitOrLoss'];
      if (methodPnLValue) {
        inheritedFields['ssmt:MethodUsedForPreparingStatementOfProfitOrLoss'] =
          methodPnLValue;
      }

      // Inherit method for preparing Statement of Comprehensive Income
      const methodCompIncomeValue =
        filingInfoData[
          'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome'
        ];
      if (methodCompIncomeValue) {
        inheritedFields[
          'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome'
        ] = methodCompIncomeValue;        
      }

      // Inherit method for preparing Statement of Cash Flows
      const methodCashFlowValue =
        filingInfoData['ssmt:MethodUsedForPreparingStatementOfCashFlows'];
      if (methodCashFlowValue) {
        inheritedFields['ssmt:MethodUsedForPreparingStatementOfCashFlows'] =
          methodCashFlowValue;
      }

      // Inherit method for representing changes in entity's equity
      const methodEquityValue =
        filingInfoData[
          'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity'
        ];
      if (methodEquityValue) {
        inheritedFields[
          'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity'
        ] = methodEquityValue;        
      }

      // Inherit comparative period values restated disclosure from Role-1
      const comparativePeriodValue =
        filingInfoData[
          'ssmt:DisclosureOnWhetherComparativePeriodValuesAreRestated'
        ];
      if (comparativePeriodValue) {
        inheritedFields[
          'ssmt:DisclosureOnWhetherComparativePeriodValuesAreRestated'
        ] = comparativePeriodValue;        
      }

      // Patch all inherited fields at once
      if (Object.keys(inheritedFields).length > 0) {
        this.scopeForm.patchValue(inheritedFields, { emitEvent: false });
      }
    } else { }
  }

  /**
   * Setup auto-save on form changes
   * Only saves to localStorage when form is VALID
   * Skips processing when in pre-validation mode to prevent infinite loops
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.scopeForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.stateService.isPreValidating() || this.isLoadingState,
      persist: () => {
        const formValue = this.scopeForm.getRawValue();
        const isValid = this.scopeForm.valid;
        this.stateService.updateSheetData(SHEET_KEYS.scopeOfFiling, formValue, isValid);
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.scopeOfFiling, { hasErrors: !this.scopeForm.valid });
      },
    });
  }

  /**
   * Setup warning evaluation on form value changes
   * Evaluates all fields with warning rules defined in the schema
   * Skips processing when in pre-validation mode to prevent infinite loops
   */
  private setupWarningEvaluation(): void {
    this.scopeForm.valueChanges
      .pipe(
        debounceTime(WARNING_EVALUATION_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Skip processing during pre-validation to prevent infinite loops
        if (this.stateService.isPreValidating()) {
          return;
        }
        this.evaluateFieldWarnings();
      });

    // ----------------------------------
    // Cross-sheet local re-validation watch.
    //
    // role-2 reads filingPrerequisites + auditorsReport in evaluateFieldWarnings()
    // (see mergedValues construction). When either upstream slice changes — via
    // SPA navigation back to scope-of-filing — the cached fieldWarnings become
    // stale. distinctUntilChanged on the JSON hash ensures self-writes (which
    // leave the upstream slice references untouched) don't re-fire.
    this.stateService.modifiedState$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.scopeOfFiling)),
        filter(() => !this.stateService.isPreValidating()),
        map(state => JSON.stringify({
          r0: state.filingPrerequisites,
          r1: state.filingInfo,
          r7: state.auditorsReport,
        })),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab safety net — when filingPrerequisites or auditorsReport is
    // mutated remotely (sibling tab edits), re-run evaluateFieldWarnings()
    // so the warning indicators stay accurate even when the user is sitting
    // on this sheet at the time of the remote update.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.scopeOfFiling)),
        filter(() => !this.stateService.isPreValidating()),
        filter(() => !!this.schema && !!this.scopeForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role2-scopeOfFiling',
          //   sourceSheetKey: sheetKey,
          // });
        }
        this.evaluateFieldWarnings();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // scopeOfFiling slice, `applyRemoteChange` rehydrates the shared state
    // and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges).
    //
    // Deliberately a SEPARATE subscription from the cross-tab safety net
    // above, but that safety net does NOT filter on `changedSheets` — it has
    // no slice filter at all, only `streamKey`/`isSheetInitializing`/
    // `isPreValidating`/schema guards — so it fires on EVERY remote
    // `modifiedState` update, including one that changed THIS sheet's own
    // slice. It is registered first, so on an own-slice remote update it
    // runs `evaluateFieldWarnings()` once against the still-STALE,
    // pre-adoption form, then this subscription runs `adoptRemoteState()`
    // (which itself re-runs the validation tail for display). The end state
    // is correct; the intermediate evaluation between the two is
    // wrong-valued for one microtask. Deliberately NOT deduped — deduping
    // risks dropping the upstream-driven refresh the safety net exists for
    // (filingPrerequisites/auditorsReport slice changes that never touch
    // `changedSheets` for scopeOfFiling at all); see fix-wave I4.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.scopeOfFiling)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.scopeOfFiling)),
        filter(() => !this.stateService.isPreValidating()),
        filter(() => !!this.schema && !!this.scopeForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role2-scopeOfFiling',
          // });
        }
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    // Date fields must be coerced BEFORE the patch, exactly as `loadSavedState()`
    // and the sheet's own API-hydration listener already do. A sibling's
    // DISCARD republishes the ORIGINAL slice — which carries the API's raw
    // "dd/MM/yyyy" strings, not the `Date` objects the modified slice holds —
    // so adopting it verbatim wrote a string into `<p-datepicker>`, which
    // renders an unparseable value as an EMPTY field. Reported on role1;
    // role2 shares the seam and owns four date fields of its own. Pinned by
    // `cross-tab-adoption-date-coercion.spec.ts`.
    adoptIntoAutoSaveSheet(
      this.scopeForm,
      this.convertDateStringsToDateObjects(
        this.stateService.getSheetData(SHEET_KEYS.scopeOfFiling)
      )
    );
    this.evaluateFieldWarnings();
  }

  /**
   * Evaluate field warnings for all fields with warning rules
   * Called on initial load and on form value changes
   */
  private evaluateFieldWarnings(): void {
    if (!this.schema || !this.scopeForm) {
      return;
    }

    // Get all form values for cross-field validations
    const allFormValues = this.scopeForm.getRawValue();

    // Get cross-sheet data for validation
    const auditorsReportData = this.stateService.getSheetData(SHEET_KEYS.auditorsReport);

    // Structure cross-sheet data for FieldWarningService.
    // Rules in role-2.form.json reference sheets via compareWith.sheet, so the
    // evaluator looks them up as allValues[sheetKey][fieldQname] — a nested
    // shape is required, not a flat spread. scopeOfFiling is self-referenced
    // by one of the rules, so its own raw values are nested under that key.
    // buildFilingInfoBagEntry() supplies the active filing-info slot under
    // both alias keys (filingPrerequisites + filingInfo) so phase-aware
    // resolution in the evaluator finds the data regardless of phase.
    const mergedValues = {
      ...allFormValues,
      ...this.stateService.buildFilingInfoBagEntry(),
      auditorsReport: auditorsReportData || {},
      scopeOfFiling: allFormValues
    };

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control) => {
      if (control.warnings && control.warnings.length > 0) {
        const formControl = this.scopeForm.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            control.warnings,
            mergedValues,
            true // Always evaluate warnings in Role-2
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          } else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });

  }

  /**
   * Get all warnings for a specific field
   * Returns array sorted: errors first, then warnings
   * Each message displayed on its own line with appropriate styling
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];

    // Add field warnings (from schema-defined warning rules, including cross-sheet date comparisons)
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => warnings.push({
      message: warning.message,
      severity: warning.severity === 'error' ? 'error' : 'warning'
    }));

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  /**
   * Check if field has warnings
   * Used to show asterisk (*) next to field label
   */
  hasWarnings(qname: string): boolean {
    return (this.fieldWarnings[qname]?.length > 0);
  }

  /**
   * Cancel changes and revert to original state
   * Network-aware: fetches from API if online, or reverts to original localStorage if offline
   */
  async onCancel(): Promise<void> {
    await this.stateService.cancelChanges();
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    await this.finalizeInit();
  }

  /**
   * Revert to saved state (original state from localStorage)
   */
  onRevert(): void {
    this.stateService.cancelSheetChanges(SHEET_KEYS.scopeOfFiling);
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    void this.finalizeInit();
    this.messageService.add({
      severity: 'info',
      summary: 'Changes Reverted',
      detail: 'Form reverted to last saved state',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Check if form has unsaved modifications
   */
  isModified(): boolean {
    return this.stateService.isSheetModified(SHEET_KEYS.scopeOfFiling);
  }

  /**
   * Clear form data
   */
  clearForm(): void {
    this.scopeForm.reset();
    this.stateService.clearSheetData(SHEET_KEYS.scopeOfFiling);

    this.messageService.add({
      severity: 'info',
      summary: 'Form Cleared',
      detail: 'All form data has been cleared',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.scopeForm.invalid) {
      this.scopeForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS,
      });
      return;
    }

    // getRawValue(), never `.value` — the Description of Business controls are
    // disabled (derived from their MSIC code), and plain `.value` drops every
    // disabled control. Every real persist path on this sheet already reads
    // getRawValue(); this one is kept in step so it cannot become a trap.
    const formData = this.scopeForm.getRawValue();

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: 'Scope of filing information has been saved successfully',
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Get dropdown options for a field
   */
  getDropdownOptions(enumSource?: string): any[] {
    if (!enumSource) return [];

    // Handle MSIC Code options
    if (enumSource === 'msicCode2008') {
      return this.msicOptions;
    }

    // Handle regular enum options
    const enumKey = enumSource.replace('type:', '');
    return this.dropdownOptions[enumKey] || [];
  }

  /**
   * Get static dropdown options
   */
  getStaticOptions(options?: string[]): any[] {
    if (!options) return [];
    return options.map((opt) => ({ label: opt, value: opt }));
  }

  /**
   * Check if field should be shown
   */
  shouldShowField(control: FormControlSchema): boolean {
    // Hide fields that are rendered in the business table
    if (control.ui.businessTable) {
      return false;
    }

    // Hide fields that are rendered in the CLBG table
    if (control.ui.clbgTable) {
      return false;
    }

    // Note: Section-based field rendering is now controlled by showInSections in schema
    // No need to manually exclude fields here anymore - they will only show in their designated sections

    // Check requiredIf condition
    if (control.requiredIf) {
      const dependentControl = this.scopeForm.get(control.requiredIf.qname);
      return dependentControl?.value === control.requiredIf.equals;
    }

    // Check requiredIfAny condition
    if (control.requiredIfAny) {
      return control.requiredIfAny.some((condition) => {
        const dependentControl = this.scopeForm.get(condition.qname);
        return dependentControl?.value === condition.equals;
      });
    }

    return true;
  }

  /**
   * Check if field is required
   */
  /**
   * Does this field's label get a `*`?
   *
   * Single-sourced through `isControlMandatory` so every sheet answers this the
   * same way. Each sheet used to hand-roll it and understood only the
   * declaration forms its own schema happened to use, which is how a mechanism
   * moved between sheets could silently stop marking. See `isControlMandatory` in `required-field.util.ts`.
   *
   * Until 2026-08-11 this method existed but NOTHING called it — the schema
   * loop rendered no marker at all, so seven mandatory fields on this sheet
   * were unmarked while three hard-coded `*` literals in the MSIC/CLBG tables
   * were the only ones visible. The template now calls it.
   */
  isFieldRequired(control: FormControlSchema): boolean {
    return isControlMandatory(control as any, { selfData: this.scopeForm.getRawValue() });
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.scopeForm.get(qname);
  }

  /**
   * Get inherited fields for "Approved Application From The Registrar Or Minister" section
   */
  getInheritedFieldsSection1(): FormControlSchema[] {
    if (!this.schema || !this.schema.controls) return [];

    const fieldQnames = [
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardToAnnualReturnOrFinancialStatementsAndReportsFromRegistrarOrMinister',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForExemptionFromCoincidingForeignSubsidiaryFinancialYearEndWithHoldingCompany',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForExemptionFromFilingFinancialStatementsAndReportsInFullXBRLFormat',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedToWaiveLodgementOfFinancialStatementsByForeignCompany',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForReliefFromRequirementsAsToFormAndContentsOfDirectorsReport',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForReliefFromRequirementsAsToFormAndContentsOfFinancialStatements',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeForCirculationOfFinancialStatementsAndReports',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeToLodgeFinancialStatementsAndReports',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeForCirculationAndHoldingAnnualGeneralMeeting',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedForExtensionOfTimeToLodgeAnnualReturn',
      'ssmt:DescriptionOnWhetherCompanyHadAppliedAnyExemptionWaiverReliefOrExtensionOfTimeWithRegardsToAnnualReturnOrFinancialStatementsAndReportsToMinister',
      'ssmt:MethodUsedForPreparingStatementOfFinancialPosition',
      'ssmt:MethodUsedForPreparingStatementOfProfitOrLoss',
      'ssmt:MethodUsedForPreparingStatementOfComprehensiveIncome',
      'ssmt:MethodUsedForPreparingStatementOfCashFlows',
      'ssmt:MethodUsedForRepresentingChangesInAnEntitysEquity',
    ];

    return this.schema.controls.filter((control) =>
      fieldQnames.includes(control.qname)
    );
  }

  /**
   * Get fields for "Changes From Subsequent XBRL Financial Statements Filing" section
   */
  getInheritedFieldsSection2(): FormControlSchema[] {
    if (!this.schema || !this.schema.controls) return [];

    const fieldQnames = [
      'ssmt:DisclosureOnWhetherComparativePeriodValuesAreRestated',
      'ssmt:DisclosureOnWhetherOpeningStatementChangedDueToChangesInAccountingStandards',
      'ssmt:DisclosureOnWhetherReclassificationOfPreviousFinancialStatementsChangedDueToChangesInAccountingStandards',
      'ssmt:DisclosureOnWhetherCompanyChangedDurationOfFinancialReportingPeriod',
    ];

    return this.schema.controls.filter((control) =>
      fieldQnames.includes(control.qname)
    );
  }

  /**
   * Type 1: Check if field should be disabled (visible but read-only)
   * Returns true if field should be disabled based on schema's disabledWhen configuration
   */
  shouldDisableField(fieldQname: string): boolean {
    const control = this.schema.controls.find((control) => control.qname === fieldQname);
    if (!control || !control.ui.disabledWhen) {
      return false;
    }

    const { field, values } = control.ui.disabledWhen;
    const filingInfoData = this.stateService.getActiveFilingInfoData();
    const triggerValue = filingInfoData?.[field];

    return values.includes(triggerValue);
  }

  /**
   * Type 2: Check if field should be hidden (not rendered)
   * Returns true if field should be completely hidden based on schema's hideWhen configuration
   *
   * For inherited fields: Also checks if field exists in Role-1 (filingInfo)
   */
  shouldHideField(fieldQname: string): boolean {
    const control = this.schema.controls.find((control) => control.qname === fieldQname);
    if (!control) {
      return false;
    }

    // Read from the active filing-info source for the current phase
    // (filingPrerequisites in Phase 1, filingInfo in Phase 2). Under PR-D's
    // strict hydration only one slot is populated, so the old hard-coded
    // 'filingPrerequisites' read returned empty in Phase 2 and hid every
    // inherited field. The phase-aware accessor resolves to the right slot.
    const filingInfoData = this.stateService.getActiveFilingInfoData();

    // For inherited (readonly-mirror) fields: hide only when the value is absent
    // from BOTH the active filing-info source AND this sheet's own value.
    //
    // Why the fallback: the 9 role2-delegated qnames (the 5 Method* fields, the
    // exemption/waiver description, stock-exchange, Bank-Negara, comparative-
    // restated) were REMOVED from the Phase-2 FilingInfoDto on the backend
    // (2026-04-27 — see FilingInfoDto.cs + role-1 `_archived.delegatedToRole2`).
    // In Phase 2 their authoritative home is the ScopeOfFiling row. A real
    // conversion still carries them in the FilingInfo *blob* as a Phase-1
    // carry-over, but a Phase-2-only conversion (e.g. a golden-data sample) never
    // ran Phase 1, so its FilingInfo lacks them and they were wrongly hidden even
    // though ScopeOfFiling holds real values. The form control is already patched
    // from ScopeOfFiling (loadSavedState / loadInheritedFields), so consult it as
    // the fallback source of truth. See docs/specifications/sheets/02_scope-of-filing.md.
    if (control.ui.inheritFrom === 'filingInfo') {
      const isBlank = (candidate: unknown): boolean =>
        candidate === undefined || candidate === null || candidate === '';
      const inheritedValue = filingInfoData?.[fieldQname];
      const ownScopeValue = this.scopeForm?.get(fieldQname)?.value;

      if (isBlank(inheritedValue) && isBlank(ownScopeValue)) {
        return true;
      }
    }

    // Schema-driven hideWhen logic
    if (control.ui.hideWhen) {
      const { field, values } = control.ui.hideWhen;
      const triggerValue = filingInfoData?.[field];

      if (values.includes(triggerValue)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Type 3: Check if field should be shown in specific section
   * Returns true if field should be shown in the given section based on schema's showInSections configuration
   */
  shouldShowInSection(fieldQname: string, sectionId: string): boolean {
    const control = this.schema.controls.find((control) => control.qname === fieldQname);
    if (!control || !control.ui.showInSections) {
      return true; // If no section config, show everywhere
    }

    return control.ui.showInSections.includes(sectionId);
  }

  /**
   * Check if CLBG table should be displayed
   * Returns true only for FS-CLBG and KFI-CLBG submission types
   */
  shouldShowCLBGTable(): boolean {
    const filingInfoData = this.stateService.getActiveFilingInfoData();
    const typeOfSubmission = filingInfoData?.['ssmt-dei:TypeOfSubmission'];
    return typeOfSubmission === 'FS-CLBG' || typeOfSubmission === 'KFI-CLBG';
  }

  /**
   * Get all visible fields for submission
   * Returns all visible fields based on schema visibility rules
   * Filters out unused fields (CLBG, scope-specific, etc.) based on submission type
   * CRITICAL: Transforms frontend bracket notation to backend nested format
   * Note: Skips verbose logging during pre-validation to avoid console spam
   */
  getVisibleFormData(): any {
    // Guard against form not being initialized yet (can happen during pre-validation)
    if (!this.scopeForm || !this.schema) {
      return {};
    }

    const allFormData = this.scopeForm.getRawValue();
    const submissionData: any = {};

    // Skip verbose logging during pre-validation to avoid console spam
    const isPreValidating = this.stateService.isPreValidating();

    // Check if CLBG table should be shown (only for FS-CLBG and KFI-CLBG)
    const isCLBGSubmission = this.shouldShowCLBGTable();

    // Dynamically collect all visible fields based on schema
    this.schema.controls.forEach((control: FormControlSchema) => {
      // Skip fields that should be hidden (based on hideWhen rules or inherited fields not in Role-0)
      if (this.shouldHideField(control.qname)) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // if (!isPreValidating) {
        //   console.log(`⏭️ Skipping hidden field: ${control.qname}`);
        // }

        return;
      }

      // Skip CLBG-specific fields if submission type is not FS-CLBG or KFI-CLBG
      if (control.ui.clbgTable && !isCLBGSubmission) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // if (!isPreValidating) {
        //   console.log(`⏭️ Skipping CLBG field (not applicable): ${control.qname}`);
        // }

        return;
      }

      // Skip scope-specific fields if they have prefix filters
      // (e.g., ssmt-scope: fields that are only for certain scenarios)
      if (control.qname.startsWith('ssmt-scope:') && !this.isScopeFieldApplicable(control)) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // if (!isPreValidating) {
        //   console.log(`⏭️ Skipping scope field (not applicable): ${control.qname}`);
        // }

        return;
      }

      // Check conditional visibility (requiredIf)
      if (control.requiredIf) {
        const dependentControl = this.scopeForm.get(control.requiredIf.qname);
        if (dependentControl?.value !== control.requiredIf.equals) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // if (!isPreValidating) {
          //   console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          // }

          return;
        }
      }

      // Check conditional visibility (requiredIfAny)
      if (control.requiredIfAny) {
        const shouldShow = control.requiredIfAny.some((condition) => {
          const dependentControl = this.scopeForm.get(condition.qname);
          return dependentControl?.value === condition.equals;
        });
        if (!shouldShow) {
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // if (!isPreValidating) {
          //   console.log(`⏭️ Skipping conditionally hidden field: ${control.qname}`);
          // }
          return;
        }
      }

      // Skip fields with empty values (optional optimization)
      // This reduces payload size by not sending empty optional fields
      const hasValue = this.fieldHasValue(control, allFormData);
      if (!control.required && !hasValue) {
        // Debug Log (Only enable when required to debug, do not allow this to land into prod)
        // if (!isPreValidating) {
        //   console.log(`⏭️ Skipping empty optional field: ${control.qname}`);
        // }
        return;
      }

      // Handle fields with dimensional axes (e.g., MSICCode with business members)
      if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
        control.ContextPolicy.allowedAxes.forEach(axis => {
          const fieldName = `${control.qname}[${axis}]`;
          if (allFormData.hasOwnProperty(fieldName)) {
            submissionData[fieldName] = allFormData[fieldName];
            // Debug Log (Only enable when required to debug, do not allow this to land into prod)
            // if (!isPreValidating) {
            //   console.log(`✅ Including dimensional field: ${fieldName} = ${allFormData[fieldName]}`);
            // }
          }
        });
      } else {
        // Regular field without axes
        const fieldName = control.qname;
        if (allFormData.hasOwnProperty(fieldName)) {
          submissionData[fieldName] = allFormData[fieldName];
          // Debug Log (Only enable when required to debug, do not allow this to land into prod)
          // if (!isPreValidating) {
          //   console.log(`✅ Including field: ${fieldName}`);
          // }
        }
      }
    });

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (!isPreValidating) {
    //   console.log('📋 Submission data (before transformation):', submissionData);
    //   console.log('📊 Total fields included:', Object.keys(submissionData).length);
    // }

    // CRITICAL: Transform frontend bracket notation to backend nested format
    // Frontend: { "ssmt:MSICCode[ssmt:BusinessOneMember]": "70209" }
    // Backend: { "ssmt:MSICCode": { "businessOneMember": "70209" } }
    const transformedData = this.xbrlTransformer.transformBusinessMembersToBackend(submissionData);
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // if (!isPreValidating) {
    //   console.log('🔄 Submission data (after transformation):', transformedData);
    // }

    return transformedData;
  }

  /**
   * Check if a scope-specific field is applicable for the current submission
   * Scope fields (ssmt-scope:) are only included if they have values or are required
   */
  private isScopeFieldApplicable(control: FormControlSchema): boolean {
    // Always include required scope fields
    if (control.required) {
      return true;
    }

    // Include scope fields that have values
    const formValue = this.scopeForm.get(control.qname)?.value;
    return formValue !== null && formValue !== undefined && formValue !== '';
  }

  /**
   * Check if a field has a meaningful value (not null, undefined, or empty string)
   */
  private fieldHasValue(control: FormControlSchema, allFormData: any): boolean {
    // Handle dimensional fields
    if (control.ContextPolicy?.allowedAxes && control.ContextPolicy.allowedAxes.length > 0) {
      return control.ContextPolicy.allowedAxes.some(axis => {
        const fieldName = `${control.qname}[${axis}]`;
        const value = allFormData[fieldName];
        return value !== null && value !== undefined && value !== '';
      });
    }

    // Handle regular fields
    const value = allFormData[control.qname];
    return value !== null && value !== undefined && value !== '';
  }

  onInputChange(event: InputEvent) {
    handleDateInputChange(event, this.scopeForm);
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.scopeForm.markAsPristine();
    void this.finalizeInit();
  }
}
